import { PlugZap } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setEmbeddedSession } from '../api/client';
import { CapabilitiesProvider } from '../components/CapabilitiesProvider';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { Note } from '../components/ErrorNote';
import { TenantTimeProvider } from '../components/TenantTimeProvider';
import { useCapabilities } from '../lib/capabilities';
import { FramedContext } from '../lib/consoleNav';
import { AnalyticsSettings } from '../screens/analytics/AnalyticsSettings';
import { manageLinkOf } from '../screens/analytics/installForm';
import { BotScreen } from '../screens/bot/BotScreen';
import { chatLinkOf } from '../screens/bot/botSetup';
import {
  type EmbedInit,
  type WebsiteHostOpens,
  type WebsiteNavigate,
  type WebsiteOpenTarget,
  WebsiteEmbedBridge,
  isWebsitePage,
} from './bridge';
import './embedWebsite.css';

/**
 * The dispatch dashboard's My website pages — Chatbot and Analytics — framed,
 * at `/website-embed?page=chatbot|analytics`.
 *
 * Both used to be reachable from the dashboard only through its Communications
 * page: Analytics as a tab of the inbox module, the Chatbot as a link out of
 * that module's settings into a new tab. Neither is a part of the inbox — they
 * are about the company's website, beside the booking widgets that already
 * live under My website — so they are framed there, as pages of their own.
 *
 * Both are SETTINGS. What they configure is read elsewhere in the dashboard:
 * the assistant's conversations in Communications, the analytics reports on
 * the Dashboard's Analytics tab. So the Analytics page is Install and settings
 * and no reports, and each page offers the way to its other half through the
 * host's router (`OPEN`) — when the host said on INIT it can open it.
 *
 * The `/help-widget` and `/dashboard-embed` pattern: the shared
 * `EmbedAuthBridge` under its own prefix (`CRM_SITE_`), NAVIGATE coming in and
 * OPEN going out. No socket, no conversation store, no phones — mounting
 * `CommsModule` to reach two settings screens would put all of that behind
 * them.
 *
 * A place inside a page rides on the address as the screens' own deep-link
 * keys — `section`/`inbox` for the Chatbot (`chatLinkOf`), `manage`/`site` for
 * an analytics install's settings (`manageLinkOf`) — so the dashboard's
 * Widgets card can open the exact install or chat its switch points at.
 *
 * The frame holds no session of its own — framing it grants nothing unless the
 * parent supplies a token, and tokens only ever flow parent → iframe. Anything
 * that must leave it (connecting another channel, which is OAuth) opens the
 * standalone console in a signed-in tab (`FramedContext`, `openConsoleTab`).
 */
export function EmbedWebsite() {
  const [session, setSession] = useState<EmbedInit | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  // The way to each page's other half, once the host has said it can open it.
  const [opens, setOpens] = useState<WebsiteHostOpens>({ conversations: false, reports: false });
  const bridgeRef = useRef<WebsiteEmbedBridge | null>(null);
  const open = useCallback((target: WebsiteOpenTarget) => bridgeRef.current?.open(target), []);
  // Seeded from the frame's own address, then held here: nobody sees a
  // frame's address, and touching the iframe's history would put section
  // switches on the HOST's Back button.
  const [to, setTo] = useState<WebsiteNavigate>(() => {
    const params = new URLSearchParams(window.location.search);
    const page = params.get('page');
    return { page: isWebsitePage(page) ? page : 'chatbot', search: window.location.search };
  });
  // A NAVIGATE is a fresh arrival: the page remounts on it, so a link to a
  // section opens THAT section rather than being read once and ignored.
  const [arrival, setArrival] = useState(0);

  useEffect(() => {
    const bridge = new WebsiteEmbedBridge();
    bridgeRef.current = bridge;
    bridge.onHost = setOpens;
    bridge.onInit = (init) => {
      setEmbeddedSession({
        tenant: init.tenant,
        getToken: bridge.getToken,
        onAuthFailed: () => bridge.notifyAuthFailed(),
      });
      setSession(init);
      setTheme(init.theme);
    };
    bridge.onTheme = setTheme;
    bridge.onNavigate = (next) => {
      setTo(next);
      setArrival((n) => n + 1);
    };
    bridge.start();
    return () => {
      bridge.dispose();
      bridgeRef.current = null;
      setEmbeddedSession(null);
    };
  }, []);

  if (!session) {
    return (
      <div className="site-embed-host" data-theme={theme}>
        <div className="site-embed-waiting">
          <PlugZap size={26} />
          <p>
            This page runs inside the dispatch dashboard, which signs it in automatically. Opened it directly? Use{' '}
            <a href={to.page === 'analytics' ? '/dashboard/analytics' : '/bot'}>the standalone console</a> instead.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="site-embed-host" data-theme={theme}>
      {/* No shell above it to catch a render crash, and a white iframe inside
          the dashboard is the least explicable of all the ways this can fail. */}
      <ErrorBoundary key={to.page} label={to.page === 'analytics' ? 'Analytics' : 'Chatbot'}>
        <TenantTimeProvider>
          <CapabilitiesProvider>
            <FramedContext.Provider value>
              {to.page === 'analytics' ? (
                <AnalyticsPage
                  key={arrival}
                  search={to.search}
                  onOpenReports={opens.reports ? () => open('reports') : null}
                />
              ) : (
                <BotScreen
                  key={arrival}
                  framed
                  link={chatLinkOf(to.search)}
                  onOpenConversations={opens.conversations ? () => open('conversations') : null}
                />
              )}
            </FramedContext.Provider>
          </CapabilitiesProvider>
        </TenantTimeProvider>
      </ErrorBoundary>
    </div>
  );
}

/**
 * Website analytics' settings on their own page — the reports are on the
 * Dashboard — opened on one install's settings when the address names one.
 * Refused on the agent's `analytics` feature exactly as the console's own
 * Dashboard section refuses it, said once rather than by every section.
 */
function AnalyticsPage({ search, onOpenReports }: { search: string; onOpenReports: (() => void) | null }) {
  const { me, loading } = useCapabilities();
  const manageLink = useMemo(() => manageLinkOf(search), [search]);
  if (me?.access.features.analytics === 'off') {
    return (
      <div className="site-embed-note">
        <Note tone="info">
          Website analytics needs the <b>Analytics</b> permission. An administrator grants it under Settings → Users
          &amp; access.
        </Note>
      </div>
    );
  }
  // Waits for /meta rather than mounting on a guess, for DashboardSection's
  // reason — only while it is in flight; one that FAILED leaves `me` null for
  // good, and then the server answers.
  if (me == null && loading) return null;
  return <AnalyticsSettings manageLink={manageLink} onOpenReports={onOpenReports} />;
}
